// Granulaire kostenpagina's die afsplitsen van /aanbouw-kosten.
export interface KostenDetail {
  /** URL-pad zonder slash, bijv. "uitbouw-kosten-per-m2". */
  slug: string;
  eyebrow: string;
  h1: string;
  title: string;
  description: string;
  socialTitle: string;
  socialDescription: string;
  shortAnswer: string;
  prijsLabel: string;
  prijs: string;
  prijsNoot: string;
  /** Prijstabel: rij-label + prijs + toelichting. */
  tabelTitel: string;
  tabel: { label: string; prijs: string; noot: string }[];
  factoren: string[];
  tips: string[];
  faq: { vraag: string; antwoord: string }[];
}

export const kostenDetails: Record<string, KostenDetail> = {
  "uitbouw-kosten-per-m2": {
    slug: "uitbouw-kosten-per-m2",
    eyebrow: "Kostengids",
    h1: "Uitbouw kosten per m² in 2026",
    title: "Uitbouw kosten per m² 2026: € 1.800 – € 3.200 per m²",
    description:
      "Wat kost een uitbouw per m² in 2026? Richtprijzen van € 1.800 tot € 3.200 per m² voor casco, standaard en luxe afwerking, met rekenvoorbeelden uit Dordrecht.",
    socialTitle: "Uitbouw kosten per m² in 2026",
    socialDescription:
      "Richtprijzen per m² voor casco, standaard en luxe uitbouw, met rekenvoorbeelden uit de Drechtsteden.",
    shortAnswer:
      "Een uitbouw kost in 2026 gemiddeld € 1.800 tot € 3.200 per m². Casco blijft rond € 1.800 per m², een compleet afgewerkte uitbouw met vloerverwarming en grote schuifpui loopt op naar € 3.200 per m². Voor een uitbouw van 20 m² komt dat neer op circa € 36.000 tot € 64.000.",
    prijsLabel: "Richtprijs per m² uitbouw",
    prijs: "€ 1.800 – € 3.200 per m²",
    prijsNoot:
      "Inclusief fundering, ruwbouw, dak en kozijnen. Exclusief keuken, meubels en eventuele paalfundering in slappe bodem.",
    tabelTitel: "Wat kost een uitbouw per oppervlakte?",
    tabel: [
      {
        label: "Uitbouw 10 m²",
        prijs: "€ 18.000 – € 32.000",
        noot: "Kleine uitbouw over de breedte van de keuken of eetkamer.",
      },
      {
        label: "Uitbouw 15 m²",
        prijs: "€ 27.000 – € 48.000",
        noot: "Meest gekozen maat: 5 meter breed en 3 meter diep.",
      },
      {
        label: "Uitbouw 20 m²",
        prijs: "€ 36.000 – € 64.000",
        noot: "Ruimte voor keuken plus eethoek met schuifpui.",
      },
      {
        label: "Uitbouw 30 m²",
        prijs: "€ 54.000 – € 96.000",
        noot: "Volledige gevelbrede uitbouw, vaak met lichtstraat.",
      },
    ],
    factoren: [
      "Afwerkingsniveau: casco, standaard of luxe",
      "Fundering: op staal of paalfundering bij slappe bodem",
      "Hoeveelheid glas: schuifpui, lichtstraat of vaste beglazing",
      "Type dak: plat dak met EPDM of een lessenaarsdak",
      "Installaties: vloerverwarming, elektra en waterpunten",
      "Bereikbaarheid van de achtertuin voor materiaal en afvoer",
    ],
    tips: [
      "Bouw dieper in plaats van breder: fundering en dak zijn de grootste vaste kosten, extra vierkante meters worden daardoor relatief goedkoper.",
      "Kies één grote schuifpui in plaats van meerdere losse kozijnen; dat scheelt kozijnwerk en levert meer daglicht.",
      "Laat de vloerverwarming en elektra meteen meelopen met de ruwbouw — achteraf openbreken kost een veelvoud.",
      "Doe het schilder- en tegelwerk zelf als je budget krap is; dat verlaagt de prijs per m² met circa € 150 tot € 250.",
    ],
    faq: [
      {
        vraag: "Wat kost een uitbouw van 20 m²?",
        antwoord:
          "Voor een uitbouw van 20 m² reken je op € 36.000 tot € 64.000, afhankelijk van afwerking, fundering en hoeveelheid glas. In Dordrecht en omgeving ligt de ondergrens vaak hoger doordat een paalfundering nodig is.",
      },
      {
        vraag: "Waarom verschilt de prijs per m² zo sterk?",
        antwoord:
          "De prijs per m² wordt vooral bepaald door het afwerkingsniveau en de fundering. Casco met een plat dak zit rond € 1.800 per m², terwijl een luxe uitbouw met lichtstraat, gevelbrede schuifpui en vloerverwarming naar € 3.200 per m² gaat.",
      },
      {
        vraag: "Wordt de prijs per m² lager bij een grotere uitbouw?",
        antwoord:
          "Ja. Fundering, dakrand en bouwaansluiting zijn grotendeels vaste kosten. Een uitbouw van 30 m² kost per m² doorgaans 10 tot 15 procent minder dan een uitbouw van 10 m².",
      },
      {
        vraag: "Zit de keuken in de prijs per m²?",
        antwoord:
          "Nee. Keuken, meubels en losse apparatuur vallen buiten de richtprijs. We zorgen wel dat de leidingen, afvoer en elektra op de juiste plek klaarliggen voor de keukenmontage.",
      },
    ],
  },
  "casco-aanbouw-kosten": {
    slug: "casco-aanbouw-kosten",
    eyebrow: "Kostengids",
    h1: "Casco aanbouw kosten in 2026",
    title: "Casco aanbouw kosten 2026: vanaf € 1.400 per m²",
    description:
      "Wat kost een casco aanbouw in 2026? Richtprijzen vanaf € 1.400 per m², wat er wel en niet in zit en hoeveel je bespaart door zelf af te werken.",
    socialTitle: "Casco aanbouw kosten in 2026",
    socialDescription:
      "Richtprijzen vanaf € 1.400 per m², plus precies wat wel en niet in een casco aanbouw zit.",
    shortAnswer:
      "Een casco aanbouw kost in 2026 gemiddeld € 1.400 tot € 2.000 per m². Je krijgt dan fundering, ruwbouw, dak, kozijnen en een wind- en waterdichte ruimte; het stuc-, tegel-, schilder- en installatiewerk doe je zelf of later. Dat bespaart doorgaans 25 tot 35 procent op de totale aanbouwkosten.",
    prijsLabel: "Richtprijs casco aanbouw",
    prijs: "€ 1.400 – € 2.000 per m²",
    prijsNoot:
      "Wind- en waterdicht opgeleverd: fundering, ruwbouw, dakconstructie met dakbedekking en kozijnen met glas.",
    tabelTitel: "Casco versus compleet afgewerkt",
    tabel: [
      {
        label: "Casco (wind- en waterdicht)",
        prijs: "€ 1.400 – € 2.000 per m²",
        noot: "Fundering, ruwbouw, dak, kozijnen en beglazing.",
      },
      {
        label: "Casco plus (met installaties)",
        prijs: "€ 1.900 – € 2.500 per m²",
        noot: "Aanvullend elektra, water, afvoer en vloerverwarming.",
      },
      {
        label: "Compleet afgewerkt",
        prijs: "€ 2.300 – € 3.200 per m²",
        noot: "Inclusief stucwerk, vloerafwerking en schilderwerk.",
      },
      {
        label: "Casco aanbouw 20 m²",
        prijs: "€ 28.000 – € 40.000",
        noot: "Meest gevraagde maat bij casco opleveren.",
      },
    ],
    factoren: [
      "Of de fundering op staal kan of dat er palen nodig zijn",
      "Constructie: houtskeletbouw of gemetselde gevel",
      "Aantal en formaat van de kozijnen en de schuifpui",
      "Type dakbedekking (EPDM, bitumen) en isolatiedikte",
      "Of de sparing in de bestaande achtergevel meegaat in het werk",
    ],
    tips: [
      "Laat de aanbouw casco opleveren maar wél mét leidingwerk; de leidingen achteraf inhakken kost meer dan je bespaart.",
      "Spreek duidelijk af wat 'casco' precies omvat — zet fundering, isolatie, dakbedekking en beglazing expliciet in de offerte.",
      "Werk zelf in de juiste volgorde af: eerst installaties, dan stuc- en vloerwerk. Anders sloop je je eigen werk weer open.",
      "Plan de afwerking direct na oplevering; een casco ruimte die maanden leeg staat vraagt om vocht- en schimmelproblemen.",
    ],
    faq: [
      {
        vraag: "Wat zit er wel en niet in een casco aanbouw?",
        antwoord:
          "In een casco aanbouw zitten fundering, vloer, wanden, dakconstructie met dakbedekking, isolatie en de kozijnen met beglazing. Niet inbegrepen: stucwerk, vloerafwerking, schilderwerk, keuken en installaties zoals elektra en vloerverwarming.",
      },
      {
        vraag: "Hoeveel bespaar ik door casco te bouwen?",
        antwoord:
          "Gemiddeld 25 tot 35 procent op de totale aanbouwkosten, mits je de afwerking echt zelf doet. Bij een aanbouw van 20 m² gaat het om een verschil van ongeveer € 12.000 tot € 20.000.",
      },
      {
        vraag: "Is een casco aanbouw geschikt voor een hypotheek of verbouwlening?",
        antwoord:
          "Ja, maar de bank kijkt naar de waarde na volledige afwerking. Vraag daarom ook een indicatie van de afwerkingskosten, zodat je totale begroting compleet is.",
      },
      {
        vraag: "Kan ik later alsnog het afwerkingswerk uitbesteden?",
        antwoord:
          "Dat kan. We leggen bij de casco-oplevering vast waar leidingen en constructie lopen, zodat een afwerker of wijzelf later zonder verrassingen verder kunnen bouwen.",
      },
    ],
  },
  "aanbouw-2-verdiepingen-kosten": {
    slug: "aanbouw-2-verdiepingen-kosten",
    eyebrow: "Kostengids",
    h1: "Aanbouw over 2 verdiepingen: kosten in 2026",
    title: "Aanbouw 2 verdiepingen kosten 2026 | Richtprijzen per m²",
    description:
      "Wat kost een aanbouw over twee verdiepingen in 2026? Richtprijzen per m², het verschil met een dakopbouw en waar je op moet letten qua vergunning en constructie.",
    socialTitle: "Aanbouw over 2 verdiepingen: kosten in 2026",
    socialDescription:
      "Richtprijzen per m² voor een aanbouw over twee lagen, plus vergunning en constructie.",
    shortAnswer:
      "Een aanbouw over twee verdiepingen kost in 2026 gemiddeld € 2.200 tot € 3.500 per m² vloeroppervlak. Voor twee lagen van elk 15 m² (30 m² totaal) reken je op circa € 66.000 tot € 105.000. Per m² is dit vaak voordeliger dan één laag, omdat fundering en dak over twee verdiepingen worden verdeeld.",
    prijsLabel: "Richtprijs aanbouw over twee lagen",
    prijs: "€ 2.200 – € 3.500 per m²",
    prijsNoot:
      "Per m² vloeroppervlak over beide verdiepingen, inclusief fundering, constructie, dak, kozijnen en trapsparing. Exclusief inrichting.",
    tabelTitel: "Rekenvoorbeelden twee verdiepingen",
    tabel: [
      {
        label: "2 × 10 m² (20 m² totaal)",
        prijs: "€ 44.000 – € 70.000",
        noot: "Extra keukenruimte beneden, slaapkamer boven.",
      },
      {
        label: "2 × 15 m² (30 m² totaal)",
        prijs: "€ 66.000 – € 105.000",
        noot: "Meest gevraagd: woonkamer-uitbreiding plus slaapkamer.",
      },
      {
        label: "2 × 20 m² (40 m² totaal)",
        prijs: "€ 88.000 – € 140.000",
        noot: "Gevelbrede aanbouw met badkamer of werkkamer boven.",
      },
      {
        label: "Extra: paalfundering",
        prijs: "€ 8.000 – € 20.000",
        noot: "Vaak nodig in de slappe bodem van de Drechtsteden.",
      },
    ],
    factoren: [
      "Constructieberekening en stalen liggers voor de tweede laag",
      "Fundering: bij twee lagen vrijwel altijd zwaarder of op palen",
      "Omgevingsvergunning — twee lagen zijn zelden vergunningvrij",
      "Aansluiting op de bestaande verdiepingsvloer en het dak",
      "Type dak: plat dak, lessenaarsdak of doorlopend schilddak",
      "Extra installaties: badkamer, radiatoren en ventilatie boven",
    ],
    tips: [
      "Laat direct een constructeur meekijken; een aangepaste stalen ligger vooraf is goedkoper dan een herberekening tijdens de bouw.",
      "Plan de trap of doorgang naar de tweede laag in het ontwerp mee — een sparing achteraf in een dragende vloer is duur.",
      "Combineer de tweede laag met een badkamer of slaapkamer; dat levert de grootste woningwaardestijging op.",
      "Vraag de vergunning ruim op tijd aan: reken op 8 tot 12 weken behandeltijd bij de gemeente.",
    ],
    faq: [
      {
        vraag: "Wat kost een aanbouw over twee verdiepingen?",
        antwoord:
          "Reken op € 2.200 tot € 3.500 per m² vloeroppervlak. Twee lagen van 15 m² komen daarmee uit op circa € 66.000 tot € 105.000, exclusief inrichting en eventuele paalfundering.",
      },
      {
        vraag: "Is een aanbouw over twee verdiepingen vergunningvrij?",
        antwoord:
          "Bijna nooit. Een uitbreiding van één laag aan de achterzijde kan vergunningvrij zijn, maar zodra je een tweede laag toevoegt is een omgevingsvergunning nodig, inclusief constructieberekening en toets aan het omgevingsplan.",
      },
      {
        vraag: "Is twee lagen goedkoper dan twee losse projecten?",
        antwoord:
          "Ja. Door fundering, steiger, dak en bouwaansluiting in één keer te doen bespaar je doorgaans 15 tot 25 procent ten opzichte van later een tweede laag toevoegen.",
      },
      {
        vraag: "Wat is voordeliger: een tweede laag of een dakopbouw?",
        antwoord:
          "Een dakopbouw is per m² vaak iets goedkoper omdat er geen fundering bij komt, maar levert minder ruimte op. Een aanbouw over twee lagen geeft zowel beneden als boven volwaardige vierkante meters.",
      },
    ],
  },
};
